import {
  AllMetrics,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  CONVERSIONS,
  COST,
  COST_DAY,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  IMPRESSIONS,
  PROFIT_GROSS,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  REVENUE,
  REVENUE_PER_CONVERSION,
  ROAS,
  TacosMetrics,
  VIEW_IMPRESSIONS,
} from '@app/core/models/metrics.constants';
import { snakeToCamelCase } from '@app/core/utilities/string.utilities';
import {
  ManageCampaignsInPGMetricsViewKeyPrefix,
  MetricsViewKeyPrefix,
  ViewableMetricsForOptimizationTypes,
} from '@app/pg-goals/pg-goals.models';

function parseToDefaultColumn(id: string, override?: Partial<TableLayoutColumn>): TableLayoutColumn {
  return { id, title: `common.metrics.${id}.tooltip`, visible: true, ...override };
}

const defaultMetricColumns: TableLayoutColumn[] = [
  COST,
  COST_DAY,
  IMPRESSIONS,
  CLICKS,
  CLICK_THROUGH_RATE,
  COST_PER_CLICK,
  CONVERSIONS,
  CONVERSION_RATE,
  COST_PER_CONVERSION,
  REVENUE_PER_CONVERSION,
  REVENUE,
  ROAS,
  COST_REVENUE_RATIO,
  RETURN_ON_INVESTMENT,
  PROFIT_GROSS,
  PROFIT_NET,
].map((id) => parseToDefaultColumn(id));
const vcpmColumns = [VIEW_IMPRESSIONS, COST_PER_MILLE].map((id) => parseToDefaultColumn(id));
const additionalKeyFiguresColumns = [...TacosMetrics].map((id) => parseToDefaultColumn(id));

const keyFiguresMetricColumns = [...defaultMetricColumns, ...additionalKeyFiguresColumns, ...vcpmColumns];
const productMetricColumns = [...defaultMetricColumns, ...vcpmColumns].filter(
  (col) => ![REVENUE_PER_CONVERSION, COST_DAY].includes(col.id),
);

export const defaultGoalsPageMetricColumns: AllMetrics[] = [CONVERSIONS, REVENUE, COST, CONVERSION_RATE];
const goalsPageMetricColumns = [
  CONVERSIONS,
  REVENUE,
  COST,
  COST_DAY,
  CONVERSION_RATE,
  CLICKS,
  ROAS,
  COST_REVENUE_RATIO,
  RETURN_ON_INVESTMENT,
  IMPRESSIONS,
  COST_PER_CONVERSION,
  CLICK_THROUGH_RATE,
  COST_PER_CLICK,
  PROFIT_NET,
  PROFIT_GROSS,
  VIEW_IMPRESSIONS,
  COST_PER_MILLE,
].map((id: AllMetrics) => parseToDefaultColumn(id, { visible: defaultGoalsPageMetricColumns.includes(id) }));

const defaultSuggestionsColumns = [CONVERSIONS, REVENUE, COST, CONVERSION_RATE];
const suggestionsMetricColumns = [
  CONVERSIONS,
  REVENUE,
  COST,
  CONVERSION_RATE,
  CLICKS,
  ROAS,
  COST_REVENUE_RATIO,
  RETURN_ON_INVESTMENT,
  IMPRESSIONS,
].map((id) => parseToDefaultColumn(id, { visible: defaultSuggestionsColumns.includes(id) }));

const defaultBudgetSuggestionsColumns = [CONVERSIONS, COST_REVENUE_RATIO];
const budgetSuggestionsMetricColumns = [
  CONVERSIONS,
  REVENUE,
  COST,
  CONVERSION_RATE,
  CLICKS,
  ROAS,
  COST_REVENUE_RATIO,
  RETURN_ON_INVESTMENT,
  IMPRESSIONS,
  VIEW_IMPRESSIONS,
  COST_PER_MILLE,
].map((id) => parseToDefaultColumn(id, { visible: defaultBudgetSuggestionsColumns.includes(id) }));

export type TableLayoutId =
  | 'keyFigures'
  | 'products'
  | 'product'
  | 'suggestions'
  | 'budgetSuggestions'
  | 'pgGoalsPage'
  | string;

export interface TableLayoutColumn {
  id: string;
  title?: string;
  visible?: boolean;
}

export interface TableLayout {
  columns: TableLayoutColumn[];
  showValues?: boolean;
  coaPrimary?: boolean;
}

export const defaultLayouts: Record<TableLayoutId, TableLayout> = {
  pgGoalsPage: { columns: [...goalsPageMetricColumns] },
  goalsForRequestDto: { columns: [...keyFiguresMetricColumns] },
  keyFigures: { columns: [...keyFiguresMetricColumns] },
  products: { columns: [...productMetricColumns] },
  product: { columns: [...productMetricColumns] },
  suggestions: { columns: [...suggestionsMetricColumns] },
  budgetSuggestions: { columns: [...budgetSuggestionsMetricColumns] },
};

Object.keys(ViewableMetricsForOptimizationTypes).forEach((key) => {
  const configs = ViewableMetricsForOptimizationTypes[key];
  const capitalKey = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();

  defaultLayouts[MetricsViewKeyPrefix + capitalKey] = {
    columns: configs.all.map((item) => parseToDefaultColumn(item, { visible: configs.default.includes(item) })),
  };

  Object.keys(configs.goals).forEach((goalType) => {
    const keyName = ManageCampaignsInPGMetricsViewKeyPrefix + capitalKey + snakeToCamelCase(goalType);

    defaultLayouts[keyName] = {
      columns: configs.all.map((item) =>
        parseToDefaultColumn(item, {
          visible: configs.goals[goalType].includes(item),
        }),
      ),
    };
  });
});
